import * as config from '../config/env.config';
import urljoin from "url-join";
import fileDownload from 'js-file-download';
import 'whatwg-fetch';
import { handleResponseAuthError } from '@/utils/handleAuthError';

export const GET = "GET";
export const POST = "POST";
export const PUT = "PUT";

// Synchronous version for legacy code (deprecated - use getAuth0Token() instead)
// DEPRECATED: Use getAuth0Token() directly instead
export const getHeaders = async () => {
  const { getAuth0Token } = await import('@/utils/auth0Token');
  const token = await getAuth0Token();
  return {
    'Authorization': 'Bearer' + ' ' + (token || ''), 
    'Accept': 'application/json',
    'X-Content-Type-Options': 'nosniff',
    'X-XSS-Protection': 1
  };
};

const getRequestConfig = async (method, requestPayload, isJson = true) => {
  const headers = await getHeaders();
  const config = {
    method,
    headers,
  };
  if (requestPayload) {
    if (isJson) {
      config.body = JSON.stringify(requestPayload);
      config.headers['Content-Type'] = 'application/json';
    }
    else config.body = requestPayload;
  }
  return config;
};

export const get = async (endpoint, stringResponse=false) => {
  try {
    const response = await fetch(endpoint, await getRequestConfig(GET, null));
    if (response && !response.ok) {
      // Handle 401/403 errors (redirects to /securitycheck)
      const handled = await handleResponseAuthError(response, endpoint);
      if (handled) {
        // Redirect was initiated, return error response
        response.error = await response.json().catch(() => ({ error: 'Unauthorized/Forbidden' }));
        return response;
      }
      // Other errors - just return error response
      response.error = await response.json();
      return response;
    }    
    response.error = false;
    response.payload = stringResponse ? await response.text() : await response.json();
    return response;
  } catch (error) {
    return Promise.reject(error.message);
  }
};

export const post = async (endpoint, requestPayload, isJson) => {  
  try {
    const response = await fetch(endpoint, await getRequestConfig(POST, requestPayload, isJson));
    if (response && !response.ok) {
      // Handle 401/403 errors (redirects to /securitycheck)
      const handled = await handleResponseAuthError(response, endpoint);
      if (handled) {
        // Redirect was initiated, return error response
        response.error = await response.json().catch(() => ({ error: 'Unauthorized/Forbidden' }));
        return response;
      }
      // Other errors - just return error response
      response.error = await response.json();
      return response;
    }    
    response.error = false;
    return response;
  } catch (error) {
    return Promise.reject(error.message);
  }
};

export const put = async (endpoint, requestPayload, isJson) => {
  try {
    const response = await fetch(endpoint, await getRequestConfig(PUT, requestPayload, isJson));
    if (response && !response.ok) {
      // Handle 401/403 errors (redirects to /securitycheck)
      const handled = await handleResponseAuthError(response, endpoint);
      if (handled) {
        // Redirect was initiated, return error response
        response.error = await response.json().catch(() => ({ error: 'Unauthorized/Forbidden' }));
        return response;
      }
      // Other errors - just return error response
      response.error = await response.json();
      return response;
    }
    response.error = false;
    return response;

  } catch (error) {
    return Promise.reject(error.message);
  }
};

export const getBlob = async (endpoint, fileName, method="get", payload=null) => {
  const { getAuth0Token } = await import('@/utils/auth0Token');
  const token = await getAuth0Token();
  const config = {
    method,
    headers: {
      'Authorization': 'Bearer' + ' ' + (token || ''),
      'X-Content-Type-Options': 'nosniff',
      'X-XSS-Protection': 1,
    },
  };
  if(payload){
    config.headers['Content-Type'] = 'application/json';
    config.body = JSON.stringify(payload);
  }
  try {
    const response = await fetch(endpoint, config);
    if (response && !response.ok) {
      // Handle 401/403 errors (redirects to /securitycheck)
      const handled = await handleResponseAuthError(response, endpoint);
      if (handled) {
        // Redirect was initiated, return error response
        response.error = await response.json().catch(() => ({ error: 'Unauthorized/Forbidden' }));
        return response;
      }
      // Other errors - just return error response
      response.error = await response.json();
      return response;
    }
    const blob = await response.blob(response);
    if (fileName) {
      fileDownload(blob, `${fileName}`);
    }
    response.error = false;
    response.payload = blob;
    return response;
  } catch (error) {
    return Promise.reject(error.message);
  }
};

export const getBlobByIds = async (endpoint, fileName, fileIds) => {
  const { getAuth0Token } = await import('@/utils/auth0Token');
  const token = await getAuth0Token();
  const config = {
    method: 'post',
    headers: {
      'Authorization': 'Bearer' + ' ' + token, 
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Content-Type-Options': 'nosniff',
      'X-XSS-Protection': 1
    },
    body: JSON.stringify({ fileIds }),
  };
  try {
    const response = await fetch(endpoint, config);
    if (response && !response.ok) {
      // Handle 401/403 errors (redirects to /securitycheck)
      const handled = await handleResponseAuthError(response, endpoint);
      if (handled) {
        // Redirect was initiated, return error response
        response.error = true;
        return response;
      }
      // Other errors - just return error response
      response.error = true;
      return response;
    }
    
    const blob = await response.blob();
    if (blob.size > 0 && fileIds.length > 0 && fileName) {
      fileDownload(blob, `${fileName}`);
      response.error = false;
      response.payload = blob;
      return response;
    }
    response.error = "No Files Downloaded";
    return response;
  } catch (error) {
    return Promise.reject(error.message);
  }
};

export const fetchCurrentUser = async (query = null) => {
  let endpoint = config.API_ENDPOINT_USERLOGIN;
  if (query) endpoint = urljoin(endpoint, query);

  return await get(endpoint);
};

export const callApi = async (endpoint, authenticated, method = 'GET', payload = null) => {
  // Use Auth0 SDK to get access token
  const { getAuth0Token } = await import('@/utils/auth0Token');
  let config = {};
  
  if(authenticated) {
    const token = await getAuth0Token();
    if (!token) {
      throw new Error("No access token available. User may not be authenticated.");
    }
    
    config = {
      headers: { 
        'Authorization': `Bearer ${token}`, 
        'X-Content-Type-Options': 'nosniff', 
        'X-XSS-Protection': 1 
      },
      method: method
    };

    if((method === 'POST' || method === 'PUT') && payload){
      config.headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify(payload);
    }
  }

  const response = await fetch(endpoint, config);

  try {
    if (response && !response.ok) {
      // Handle 401/403 errors (redirects to /securitycheck)
      const handled = await handleResponseAuthError(response, endpoint);
      if (handled) {
        // Redirect was initiated, reject with error
        const error = await response.json().catch(() => ({ error: 'Unauthorized/Forbidden' }));
        return Promise.reject(error);
      }
      // Other errors - reject normally
      const error = await response.json();
      return Promise.reject(error);
    }
    return await response.json();
  }
  catch(err) {
    return Promise.reject(err.message);
  }
};
